/*
  FAQ page behaviour, shared by the movitamins-faq-* sections:
  - the banner search filters the questions on the page (question and answer text, accents ignored);
  - suggestion buttons fill in the search;
  - topic links clear an active search before jumping, and the side menu follows the topic in view;
  - a link to a question (#faq-…) opens it.
  Without questions on the page the search form falls back to the store search.
*/
(() => {
  if (window.MovitaminsFaq) return;

  const normalize = (value) =>
    (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Light Dutch word forms, so "inloggen" also finds "inlogt" and "betalen" finds "betaalt":
  // drop the -en ending, undouble a final consonant (logg → log) and double a single vowel (betal → betaal).
  const variants = (term) => {
    const list = [term];
    if (term.length > 5 && term.endsWith('en')) {
      let base = term.slice(0, -2);
      if (/([bcdfgklmnprstvz])\1$/.test(base)) base = base.slice(0, -1);
      list.push(base);
      const single = base.match(/^(.*[^aeiou])([aeiou])([^aeiou])$/);
      if (single) list.push(single[1] + single[2] + single[2] + single[3]);
    }
    return list;
  };

  const state = { query: '' };

  const items = () => [...document.querySelectorAll('[data-faq-item]')];
  const groups = () => [...document.querySelectorAll('[data-faq-group]')];

  function highlight(item, terms) {
    const label = item.querySelector('[data-faq-question]');
    if (!label) return;
    if (label.dataset.text === undefined) label.dataset.text = label.textContent;
    const text = label.dataset.text;
    const rawTerms = terms.filter((term) => term.length > 2);
    if (!rawTerms.length) {
      label.textContent = text;
      return;
    }
    const pattern = new RegExp(`(${rawTerms.map(escapeRegExp).join('|')})`, 'gi');
    const fragment = document.createDocumentFragment();
    text.split(pattern).forEach((part, index) => {
      if (!part) return;
      if (index % 2 === 1) {
        const mark = document.createElement('mark');
        mark.textContent = part;
        fragment.append(mark);
      } else {
        fragment.append(part);
      }
    });
    label.replaceChildren(fragment);
  }

  function setQuery(query) {
    state.query = query;
    const terms = normalize(query).split(/\s+/).filter((term) => term.length > 1).map(variants);
    const marks = terms.flat();
    const active = terms.length > 0;
    let total = 0;

    groups().forEach((group) => {
      let count = 0;
      group.querySelectorAll('[data-faq-item]').forEach((item) => {
        if (item.dataset.search === undefined) item.dataset.search = normalize(item.textContent);
        const match = !active || terms.every((forms) => forms.some((form) => item.dataset.search.includes(form)));
        item.hidden = !match;
        highlight(item, match && active ? marks : []);
        if (match) count += 1;
      });
      group.hidden = count === 0;
      total += count;
      document.querySelectorAll(`[data-faq-nav-link="${group.id}"]`).forEach((link) => {
        link.closest('li').hidden = count === 0;
      });
    });

    // Few results: open them, so the answers show straight away. Close them again once the search changes.
    items().forEach((item) => {
      if (item.dataset.autoOpened) {
        item.open = false;
        delete item.dataset.autoOpened;
      }
    });
    if (active && total > 0 && total <= 3) {
      items().forEach((item) => {
        if (item.hidden || item.open) return;
        item.open = true;
        item.dataset.autoOpened = 'true';
      });
    }

    document.querySelectorAll('[data-faq-topics]').forEach((el) => {
      el.hidden = active;
    });
    document.querySelectorAll('[data-faq-results]').forEach((el) => {
      el.hidden = !active || total === 0;
      const text = el.querySelector('[data-faq-results-text]');
      if (text) {
        const template = total === 1 ? el.dataset.one : el.dataset.other;
        text.textContent = template.replace('[count]', total).replace('[query]', query.trim());
      }
    });
    document.querySelectorAll('[data-faq-empty]').forEach((el) => {
      el.hidden = !(active && total === 0);
      const text = el.querySelector('[data-faq-empty-query]');
      if (text) text.textContent = query.trim();
    });
    document.querySelectorAll('[data-faq-search] input').forEach((input) => {
      if (input.value !== query) input.value = query;
      const clear = input.closest('[data-faq-search]').querySelector('[data-faq-clear]');
      if (clear) clear.hidden = !query;
    });
  }

  function scrollToList() {
    const list = document.querySelector('[data-faq-list]');
    if (list) list.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  function openFromHash() {
    const id = decodeURIComponent(location.hash.slice(1));
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    if (state.query) setQuery('');
    if (target.matches('[data-faq-item]')) {
      target.open = true;
      target.scrollIntoView();
    }
  }

  function bindSearch(form) {
    if (form.dataset.faqBound) return;
    form.dataset.faqBound = 'true';
    const input = form.querySelector('input');
    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => setQuery(input.value), 120);
    });
    form.addEventListener('submit', (event) => {
      // Without questions on the page, the form searches the store.
      if (!items().length) return;
      event.preventDefault();
      clearTimeout(timer);
      setQuery(input.value);
      if (input.value.trim()) scrollToList();
    });
  }

  function bindPage() {
    document.querySelectorAll('[data-faq-search]').forEach(bindSearch);
  }

  document.addEventListener('click', (event) => {
    const clear = event.target.closest('[data-faq-clear]');
    if (clear) {
      setQuery('');
      const input = document.querySelector('[data-faq-search] input');
      if (input) input.focus();
      return;
    }

    const suggestion = event.target.closest('[data-faq-suggest]');
    if (suggestion) {
      setQuery(suggestion.dataset.faqSuggest);
      scrollToList();
      return;
    }

    // Topic links: clear a search first, otherwise the topic may be hidden.
    const topicLink = event.target.closest('a[href^="#"]');
    if (topicLink && state.query && topicLink.closest('.mvfaq')) {
      setQuery('');
    }
  });

  window.addEventListener('hashchange', openFromHash);

  // Side menu follows the topic in view.
  let observer;
  function watchGroups() {
    if (observer) observer.disconnect();
    if (!('IntersectionObserver' in window)) return;
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          document.querySelectorAll('[data-faq-nav-link]').forEach((link) => {
            link.setAttribute('aria-current', String(link.dataset.faqNavLink === entry.target.id));
          });
        });
      },
      { rootMargin: '-25% 0px -65% 0px' }
    );
    groups().forEach((group) => observer.observe(group));
  }

  function init() {
    bindPage();
    watchGroups();
    const query = new URLSearchParams(location.search).get('q');
    if (query && items().length) {
      setQuery(query);
    } else {
      openFromHash();
    }
  }

  window.MovitaminsFaq = { setQuery };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', () => {
    init();
    if (state.query) setQuery(state.query);
  });
})();
